import orderService from '@api/services/orderService';
import CommonModels, { IdRequest } from 'respona_api/generated/common_pb';
import request from '@api/base/request';
import {
  BacklinksSortType,
  BacklinksType,
  ConcernAttachedFileType,
  ConcernType,
  OrderDetailsType,
  OrderItemRequestType,
  OrderItemType,
  OrderPricingType,
  OrderType,
  ExcludedDomainType,
  ListWebhookDeliveriesResponseType,
  WebhookDeliveryViewType,
  DomainApprovalReviewType,
  DomainApprovalReviewSubmitResultType,
  DomainDecisionType,
} from '@ts/orders.types';
import {
  AddConcernMessageRequest,
  ConcernAttachedFile,
  ConcernAreaMap,
  GetConcernRequest,
  OrderArchiveStateRequest,
  OrderBacklinkExportRequest,
  OrderBacklinkFilter,
  OrderBacklinkFilterFieldMap,
  OrderBacklinkPageRequest,
  OrderCreateRequest,
  OrderFilter,
  OrderFilterFieldMap,
  OrderItemContainerRequest,
  OrderItemRequest,
  OrderPageRequest,
  OrderRenameRequest,
  OrderUpdateRequest,
  RaiseConcernRequest,
  ExcludedDomainBatchRequest,
  ExcludedDomainRequest,
  ExcludedDomainsFilterRequest,
  ExcludedDomainFilter,
  ExcludedDomainFilterFieldMap,
  ListWebhookDeliveriesRequest,
  RedeliverWebhookRequest,
  DomainApprovalReviewRequest,
  DomainApprovalReviewSubmitRequest,
  DomainDecision,
} from 'respona_api/generated/order-concierge_pb';
import * as common_pb from 'respona_api/generated/common_pb';
import streamRequest from '@api/base/streamRequest';
import {
  FileResponseContainerType,
  FileResponseType,
  FolderType,
  IdResponseType,
} from '@ts/common.types';
import * as google_protobuf_empty_pb from 'google-protobuf/google/protobuf/empty_pb';
import { FilterFieldType } from '@ts/filterField.type';
import { DEFAULT_BACKLINKS_SORT } from '@constants/orders.constants';

/** ------------------------------- PRICING API ------------------------------------------------ */

export const getOrderPricingApi = (): Promise<OrderPricingType[]> => {
  const service = orderService.getInstance();
  const emptyRequest = new google_protobuf_empty_pb.Empty();
  return streamRequest<OrderPricingType[]>(service, service.getOrderPricing, emptyRequest);
};

/** -------------------------------- ORDERS API ------------------------------------------------ */

export const getOrdersApi = (
  workspaceId: number,
  page: number,
  limit: number,
  filters: FilterFieldType<OrderFilterFieldMap>[]
): Promise<OrderType[]> => {
  const service = orderService.getInstance();
  const pageRequest = new OrderPageRequest();
  pageRequest.setPage(page);
  pageRequest.setWorkspaceId(workspaceId);
  pageRequest.setLimit(limit);
  if (filters?.length > 0) {
    pageRequest.setFiltersList(
      filters.map((it) => {
        const filter = new OrderFilter();
        filter.setJoin(it.join);
        filter.setOperation(it.operation);
        filter.setOrder(it.order);
        filter.setField(it.field);
        if (typeof it.value === 'object' && it.value !== null) {
          // @ts-ignore
          filter.setValue(it.value.value.toString());
        } else {
          filter.setValue(it.value.toString());
        }
        return filter;
      })
    );
  }

  return streamRequest<OrderType[]>(service, service.getOrders, pageRequest);
};

export const getOpenOrderApi = (
  workspaceId: number,
  orderId: number
): Promise<OrderDetailsType> => {
  const service = orderService.getInstance();
  const idRequest = new CommonModels.IdRequest();

  idRequest.setId(orderId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<OrderDetailsType>(service, service.openOrder, idRequest);
};

export const updateOrderArchiveStateApi = (
  workspaceId: number,
  orderId: number,
  archived: boolean
): Promise<void> => {
  const service = orderService.getInstance();
  const idRequest = new OrderArchiveStateRequest();

  idRequest.setOrderId(orderId);
  idRequest.setWorkspaceId(workspaceId);
  idRequest.setState(archived);

  return request<void>(service, service.updateOrderArchiveState, idRequest);
};

export const createOrderApi = (workspaceId: number, folderId?: number): Promise<IdResponseType> => {
  const service = orderService.getInstance();
  const createOrderRequest = new OrderCreateRequest();

  createOrderRequest.setWorkspaceId(workspaceId);
  if (folderId) {
    createOrderRequest.setOptionalFolderId(folderId);
  }

  return request<IdResponseType>(service, service.createOrder, createOrderRequest);
};

export const renameOrderApi = (
  workspaceId: number,
  orderId: number,
  title: string
): Promise<OrderDetailsType> => {
  const service = orderService.getInstance();
  const updateOrderRequest = new OrderRenameRequest();
  updateOrderRequest.setId(orderId);
  updateOrderRequest.setWorkspaceId(workspaceId);
  updateOrderRequest.setTitle(title);

  return request<OrderDetailsType>(service, service.renameOrder, updateOrderRequest);
};

export const generatePdfApi = (orderId: number, workspaceId: number): Promise<FileResponseType> => {
  const service = orderService.getInstance();
  const idRequest = new CommonModels.IdRequest();
  idRequest.setId(orderId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<FileResponseType>(service, service.generatePdf, idRequest);
};

export const updateOrderApi = (
  workspaceId: number,
  orderId: number,
  title: string,
  deliveryType: common_pb.OrderDeliveryTypeMap[keyof common_pb.OrderDeliveryTypeMap],
  approvalType?: common_pb.OrderApprovalTypeMap[keyof common_pb.OrderApprovalTypeMap],
  guidelineType?: common_pb.OrderContentGuidelineTypeMap[keyof common_pb.OrderContentGuidelineTypeMap],
  domainSpecification?: string,
  contentGuidelines?: string,
  publisherType?: common_pb.OrderPublisherTypeMap[keyof common_pb.OrderPublisherTypeMap],
): Promise<OrderDetailsType> => {
  const service = orderService.getInstance();
  const updateOrderRequest = new OrderUpdateRequest();
  updateOrderRequest.setId(orderId);
  updateOrderRequest.setWorkspaceId(workspaceId);
  updateOrderRequest.setTitle(title);
  updateOrderRequest.setDeliveryType(deliveryType);

  if (approvalType !== undefined) {
    updateOrderRequest.setApprovalType(approvalType);
  }

  if (guidelineType !== undefined) {
    updateOrderRequest.setGuidelineType(guidelineType);
  }

  if (domainSpecification !== undefined) {
    updateOrderRequest.setDomainSpecification(domainSpecification);
  }

  if (contentGuidelines !== undefined) {
    updateOrderRequest.setContentGuidelines(contentGuidelines);
  }

  if (publisherType !== undefined) {
    updateOrderRequest.setPublisherType(publisherType);
  }

  return request<OrderDetailsType>(service, service.updateOrder, updateOrderRequest);
};

export const deleteOrderApi = (workspaceId: number, orderId: number): Promise<void> => {
  const service = orderService.getInstance();
  const idRequest = new CommonModels.IdRequest();

  idRequest.setId(orderId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<void>(service, service.deleteOrder, idRequest);
};

export const duplicateOrderApi = (workspaceId: number, orderId: number): Promise<void> => {
  const service = orderService.getInstance();
  const idRequest = new CommonModels.IdRequest();

  idRequest.setId(orderId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<void>(service, service.duplicateOrder, idRequest);
};

export const launchOrderApi = (workspaceId: number, orderId: number): Promise<void> => {
  const service = orderService.getInstance();
  const idRequest = new CommonModels.IdRequest();

  idRequest.setId(orderId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<void>(service, service.launchOrder, idRequest);
};

export const updateOrderFolderRelationApi = (
  workspaceId: number,
  orderId: number,
  folderId: number
): Promise<void> => {
  const service = orderService.getInstance();
  const idRequest = new CommonModels.IdRequest();

  idRequest.setId(orderId);
  idRequest.setOptionalRelatedId(folderId);
  idRequest.setOptionalWorkspaceId(workspaceId);

  return request<void>(service, service.updateOrderFolderRelation, idRequest);
};

/** ---------------------------- BACKLINK ITEMS API -------------------------------------------- */

const buildOrderBacklinkFilters = (
  filters: FilterFieldType<OrderBacklinkFilterFieldMap>[]
): OrderBacklinkFilter[] =>
  filters.map((it) => {
    const filterRequest = new OrderBacklinkFilter();
    filterRequest.setJoin(it.join);
    filterRequest.setOperation(it.operation);
    filterRequest.setOrder(it.order);
    filterRequest.setField(it.field);
    if (Object.prototype.hasOwnProperty.call(it.value, 'value')) {
      // @ts-ignore
      filterRequest.setValue(it.value.value.toString());
    } else {
      filterRequest.setValue(it.value.toString());
    }
    return filterRequest;
  });

export const getOrderBacklinksApi = (
  workspaceId: number,
  page: number,
  limit: number,
  filters: FilterFieldType<OrderBacklinkFilterFieldMap>[],
  sort: BacklinksSortType = DEFAULT_BACKLINKS_SORT
): Promise<BacklinksType[]> => {
  const service = orderService.getInstance();
  const pageRequest = new OrderBacklinkPageRequest();
  pageRequest.setWorkspaceId(workspaceId);
  pageRequest.setPage(page);
  pageRequest.setFiltersList(buildOrderBacklinkFilters(filters));
  pageRequest.setLimit(limit);
  pageRequest.setSortField(sort.field);
  pageRequest.setSortDirection(sort.direction);

  return streamRequest<BacklinksType[]>(service, service.getOrderBacklinks, pageRequest);
};

export const exportOrderBacklinksToCsvApi = (
  workspaceId: number,
  selectedIds?: number[],
  filters?: FilterFieldType<OrderBacklinkFilterFieldMap>[],
  sort: BacklinksSortType = DEFAULT_BACKLINKS_SORT
): Promise<FileResponseContainerType[]> => {
  const service = orderService.getInstance();
  const pageRequest = new OrderBacklinkExportRequest();
  pageRequest.setWorkspaceId(workspaceId);
  // The server applies the sort to all three export types, so the CSV comes out in the
  // same order as the grid the user exported it from.
  pageRequest.setSortField(sort.field);
  pageRequest.setSortDirection(sort.direction);
  if (filters?.length > 0) {
    pageRequest.setType(OrderBacklinkExportRequest.ExportType.FILTERED);
    pageRequest.setFiltersList(buildOrderBacklinkFilters(filters));
  } else if (selectedIds?.length > 0) {
    pageRequest.setType(OrderBacklinkExportRequest.ExportType.SELECTED);
    pageRequest.setSelectedIdsList(selectedIds);
  } else {
    pageRequest.setType(OrderBacklinkExportRequest.ExportType.ALL);
  }
  return streamRequest<FileResponseContainerType[]>(
    service,
    service.exportOrderBacklinksToCsv,
    pageRequest
  );
};

/** -------------------------------- CONCERNS API ---------------------------------------------- */

const buildConcernAttachedFiles = (
  files?: ConcernAttachedFileType[]
): ConcernAttachedFile[] => {
  if (!files?.length) {
    return [];
  }
  return files.map((file) => {
    const attached = new ConcernAttachedFile();
    attached.setUid(file.uid);
    attached.setName(file.name);
    attached.setType(file.type);
    attached.setSize(file.size);
    return attached;
  });
};

export const raiseOrderItemConcernApi = (
  workspaceId: number,
  orderItemId: number,
  areas: ConcernAreaMap[keyof ConcernAreaMap][],
  message: string,
  attachedFiles?: ConcernAttachedFileType[]
): Promise<ConcernType> => {
  const service = orderService.getInstance();
  const raiseRequest = new RaiseConcernRequest();

  raiseRequest.setOrderItemId(orderItemId);
  raiseRequest.setWorkspaceId(workspaceId);
  raiseRequest.setAreasList(areas);
  raiseRequest.setMessage(message);
  raiseRequest.setAttachedFilesList(buildConcernAttachedFiles(attachedFiles));

  return request<ConcernType>(service, service.raiseOrderItemConcern, raiseRequest);
};

export const getOrderItemConcernApi = (
  workspaceId: number,
  orderItemId: number
): Promise<ConcernType> => {
  const service = orderService.getInstance();
  const getRequest = new GetConcernRequest();

  getRequest.setOrderItemId(orderItemId);
  getRequest.setWorkspaceId(workspaceId);

  return request<ConcernType>(service, service.getOrderItemConcern, getRequest);
};

export const addClientConcernMessageApi = (
  workspaceId: number,
  concernId: number,
  body: string,
  attachedFiles?: ConcernAttachedFileType[]
): Promise<ConcernType> => {
  const service = orderService.getInstance();
  const addMessageRequest = new AddConcernMessageRequest();

  addMessageRequest.setConcernId(concernId);
  addMessageRequest.setWorkspaceId(workspaceId);
  addMessageRequest.setBody(body);
  addMessageRequest.setAttachedFilesList(buildConcernAttachedFiles(attachedFiles));

  return request<ConcernType>(service, service.addClientConcernMessage, addMessageRequest);
};

export const getOrCreateOnboardingOrderApi = (
  workspaceId: number,
): Promise<OrderDetailsType> => {
  const service = orderService.getInstance();
  const orderRequest = new IdRequest();
  orderRequest.setOptionalWorkspaceId(workspaceId);

  return request<OrderDetailsType>(service, service.getOrCreateOnboardingOrder, orderRequest);
};

/** ------------------------------- ORDER ITEMS API -------------------------------------------- */

export const getOrderItemsApi = (
  orderId: number,
  workspaceId: number,
  page: number,
  limit: number
): Promise<OrderItemType[]> => {
  const service = orderService.getInstance();
  const pageRequest = new CommonModels.PageRequest();
  pageRequest.setOptionalEntityId(orderId);
  pageRequest.setPage(page);
  pageRequest.setOptionalWorkspaceId(workspaceId);
  pageRequest.setLimit(limit);

  return streamRequest<OrderItemType[]>(service, service.getOrderItems, pageRequest);
};

export const createOrderItemsApi = (
  orderId: number,
  workspaceId: number,
  items: OrderItemRequestType[]
): Promise<OrderItemType[]> => {
  const service = orderService.getInstance();
  const createOrderItemRequest = new OrderItemContainerRequest();

  createOrderItemRequest.setOrderId(orderId);
  createOrderItemRequest.setWorkspaceId(workspaceId);
  createOrderItemRequest.setItemsList(
    items.map((item) => {
      const requestItem = new OrderItemRequest();
      requestItem.setUid(item.uid);
      requestItem.setId(item.id);
      requestItem.setRequestedAnchor(item.requestedAnchor);
      requestItem.setRequestedTargetUrl(item.requestedTargetUrl);
      requestItem.setContentGuidelines(item.contentGuidelines);
      requestItem.setDomainSpecification(item.domainSpecification);
      requestItem.setQuality(item.quality);
      return requestItem;
    })
  );

  return streamRequest<OrderItemType[]>(service, service.createOrderItems, createOrderItemRequest);
};

export const updateOrderItemsApi = (
  orderId: number,
  workspaceId: number,
  items: OrderItemRequestType[]
): Promise<OrderItemType[]> => {
  const service = orderService.getInstance();
  const updateOrderItemRequest = new OrderItemContainerRequest();

  updateOrderItemRequest.setOrderId(orderId);
  updateOrderItemRequest.setWorkspaceId(workspaceId);
  updateOrderItemRequest.setItemsList(
    items.map((item) => {
      const requestItem = new OrderItemRequest();
      requestItem.setUid(item.uid);
      requestItem.setId(item.id);
      requestItem.setRequestedAnchor(item.requestedAnchor);
      requestItem.setRequestedTargetUrl(item.requestedTargetUrl);
      requestItem.setContentGuidelines(item.contentGuidelines);
      requestItem.setDomainSpecification(item.domainSpecification);
      requestItem.setQuality(item.quality);
      return requestItem;
    })
  );

  return streamRequest<OrderItemType[]>(service, service.updateOrderItems, updateOrderItemRequest);
};

export const deleteOrderItemsApi = (
  workspaceId: number,
  orderId: number,
  itemsId: number[]
): Promise<void> => {
  const service = orderService.getInstance();
  const deleteOrderItemRequest = new CommonModels.IdContainer();

  deleteOrderItemRequest.setOptionalWorkspaceId(workspaceId);
  deleteOrderItemRequest.setOptionalRelatedId(orderId);
  deleteOrderItemRequest.setIdList(itemsId);

  return request<void>(service, service.deleteOrderItems, deleteOrderItemRequest);
};

/** ------------------------------- ORDER FOLDERS API -------------------------------------------- */

export const createOrderFolderApi = (
  workspaceId: number,
  folderTitle: string
): Promise<FolderType> => {
  const client = orderService.getInstance();
  const folderRequest = new CommonModels.Folder();

  folderRequest.setRelatedid(workspaceId);
  folderRequest.setTitle(folderTitle);

  return request<FolderType>(client, client.createOrderFolder, folderRequest);
};

export const getAllOrderFoldersApi = (
  workspaceId: number,
  page: number,
  limit: number
): Promise<FolderType[]> => {
  const client = orderService.getInstance();
  const pageRequest = new CommonModels.PageRequest();
  pageRequest.setOptionalWorkspaceId(workspaceId);
  pageRequest.setPage(page);
  pageRequest.setLimit(limit);

  return streamRequest<FolderType[]>(client, client.getAllOrderFolders, pageRequest);
};

export const updateOrderFolderApi = (
  workspaceId: number,
  folderId: number,
  folderTitle: string
): Promise<FolderType> => {
  const client = orderService.getInstance();
  const folderRequest = new CommonModels.Folder();

  folderRequest.setRelatedid(workspaceId);
  folderRequest.setId(folderId);
  folderRequest.setTitle(folderTitle);

  return request<FolderType>(client, client.updateOrderFolder, folderRequest);
};

export const removeOrderFolderApi = (workspaceId: number, folderId: number) => {
  const client = orderService.getInstance();
  const removeRequest = new CommonModels.IdRequest();

  removeRequest.setOptionalWorkspaceId(workspaceId);
  removeRequest.setId(folderId);

  return request(client, client.deleteOrderFolder, removeRequest);
};

/** ------------------------------- EXCLUDED DOMAINS API -------------------------------------------- */

export const addDomainsToExcludedListApi = (
  workspaceIds: number[],
  domains: string[]
): Promise<void> => {
  const service = orderService.getInstance();
  const requestBody = new ExcludedDomainBatchRequest();
  requestBody.setWorkspaceIdsList(workspaceIds);
  requestBody.setDomainsList(domains);
  return request<void>(service, service.addDomainsToExcludedList, requestBody);
};

export const updateExcludedDomainApi = (
  id: number,
  domain: string,
  workspaceIds: number[]
): Promise<void> => {
  const service = orderService.getInstance();
  const requestBody = new ExcludedDomainRequest();
  requestBody.setId(id);
  requestBody.setDomain(domain);
  requestBody.setWorkspaceIdsList(workspaceIds);
  return request<void>(service, service.updateExcludedDomain, requestBody);
};

export const removeDomainsFromExcludedListApi = (
  workspaceId: number,
  ids: number[]
): Promise<void> => {
  const service = orderService.getInstance();
  const requestBody = new CommonModels.IdContainer();
  requestBody.setOptionalWorkspaceId(workspaceId);
  requestBody.setIdList(ids);
  return request<void>(service, service.removeDomainsFromExcludedList, requestBody);
};

export const getExcludedDomainsApi = (
  page: number,
  limit: number,
  optionalQuery: string,
  filters: FilterFieldType<ExcludedDomainFilterFieldMap>[]
): Promise<ExcludedDomainType[]> => {
  const service = orderService.getInstance();
  const requestBody = new ExcludedDomainsFilterRequest();
  requestBody.setPage(page);
  requestBody.setLimit(limit);
  if (optionalQuery) {
    requestBody.setOptionalQuery(optionalQuery);
  }
  if (filters?.length > 0) {
    requestBody.setOptionalFiltersList(
      filters.map((it) => {
        const filter = new ExcludedDomainFilter();
        filter.setJoin(it.join);
        filter.setOperation(it.operation);
        filter.setOrder(it.order);
        filter.setField(it.field);
        if (typeof it.value === 'object' && it.value !== null) {
          // @ts-ignore
          filter.setValue(it.value.value.toString());
        } else {
          filter.setValue(it.value.toString());
        }
        return filter;
      })
    );
  }
  return streamRequest<ExcludedDomainType[]>(service, service.getExcludedDomains, requestBody);
};

/** ---------------------------- DOMAIN APPROVAL API ------------------------------------------- */

export const getDomainApprovalReviewApi = (
  workspaceId: number,
  orderItemId: number
): Promise<DomainApprovalReviewType> => {
  const service = orderService.getInstance();
  const reviewRequest = new DomainApprovalReviewRequest();

  reviewRequest.setOrderItemId(orderItemId);
  reviewRequest.setWorkspaceId(workspaceId);

  return request<DomainApprovalReviewType>(
    service,
    service.getDomainApprovalReview,
    reviewRequest
  );
};

export const submitDomainApprovalReviewApi = (
  workspaceId: number,
  orderItemId: number,
  decisions: DomainDecisionType[],
  comment?: string,
  // RES-7256 A2: the client's placement-domain choice, required when >1 domain is approved.
  primarySuggestionId?: number
): Promise<DomainApprovalReviewSubmitResultType> => {
  const service = orderService.getInstance();
  const submitRequest = new DomainApprovalReviewSubmitRequest();

  submitRequest.setOrderItemId(orderItemId);
  submitRequest.setWorkspaceId(workspaceId);
  if (primarySuggestionId > 0) {
    submitRequest.setPrimarySuggestionId(primarySuggestionId);
  }
  submitRequest.setDecisionsList(
    decisions.map((decision) => {
      const decisionRequest = new DomainDecision();
      decisionRequest.setSuggestionId(decision.suggestionId);
      decisionRequest.setApproved(decision.approved);
      if (decision.optionalReason?.length > 0) {
        decisionRequest.setOptionalReason(decision.optionalReason);
      }
      return decisionRequest;
    })
  );
  if (comment?.length > 0) {
    submitRequest.setOptionalComment(comment);
  }

  return request<DomainApprovalReviewSubmitResultType>(
    service,
    service.submitDomainApprovalReview,
    submitRequest
  );
};

/** ---------------------------- WEBHOOK DELIVERIES API ---------------------------------------- */

export const listWebhookDeliveriesApi = (
  webhookEndpointId: number,
  page: number,
  limit: number
): Promise<ListWebhookDeliveriesResponseType> => {
  const service = orderService.getInstance();
  const listRequest = new ListWebhookDeliveriesRequest();

  listRequest.setWebhookEndpointId(webhookEndpointId);
  listRequest.setPage(page);
  listRequest.setLimit(limit);

  return request<ListWebhookDeliveriesResponseType>(
    service,
    service.listWebhookDeliveries,
    listRequest
  );
};

export const redeliverWebhookApi = (deliveryUid: string): Promise<WebhookDeliveryViewType> => {
  const service = orderService.getInstance();
  const redeliverRequest = new RedeliverWebhookRequest();

  redeliverRequest.setDeliveryUid(deliveryUid);

  return request<WebhookDeliveryViewType>(service, service.redeliverWebhook, redeliverRequest);
};
